import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { format } from 'date-fns'
import { clone } from 'ramda'
import * as Sentry from '@sentry/react'
import { setIsDrops } from '../../modules/miscUi/actions'
import { Box } from '../../shared_components/UI/Box'
import { selectAccount } from '../../modules/web3/selectors'
import {
  AllocationCard,
  Button,
  ChainIcon,
  Logo,
  LogoAndTitle,
  TokenName,
  TopBar,
} from './Allocations/styles'

import AnimatedLoader from '../../shared_components/AnimatedLoader'

import NoAllocations from './Allocations/NoAllocations'
import { IDrop, RewardSettings } from '../../types/reducerTypes'
import { getRgbaValue } from '../../utils/styling'
import {
  useClaimRewardsHistoryMutation,
  useClaimRewardsSignatureMutation,
  useLazyGetDropsRewardsQuery,
} from '../../modules/user/api'
import { showDecimalsIfNeeded } from './Allocations/InvestmentInfo'
import { ThemedButton } from '../../shared_components/ThemedButton'
import { useGetContractByNameQuery } from '../../modules/web3/api'
import { TooltipText } from '../DropDetails/ChapterView'
import useNetworkId from '../../hooks/useNetworkId'
import { BLAST } from '../../constants'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../shared_components/Library/Buttons/ConnectWalletButton'
import SwitchToBlastButton from '../../shared_components/Library/Buttons/SwitchToBlastButton'
import SwitchToBaseButton from '../../shared_components/Library/Buttons/SwitchToBaseButton'
import useWeb3 from '../../hooks/useWeb3'
import { ethers } from 'ethers'

export type Reward = {
  id: number
  accountId: number
  projectId: number
  walletAddress: string
  amount: number
  createdAt: string
  updatedAt: string
  metadata: {
    drop: {
      rank: number
      level: number
      totalXp: number
      accountId: number
      distribution: {
        m: number
        n: number
        minLevel: number
      }
      userWeighting: number
      collectibleRandomNumber?: number
      reward: RewardSettings
    }
  }
}

type ClaimHistory = {
  id: number
  walletAddress: string
  dropId: number
  amount: number
  transactionHash?: string
  createdAt: string
  updatedAt: string
}

type RewardWithHistory = {
  dropReward: RewardSettings
  userReward: Reward
  history?: ClaimHistory
} | null

export type DropWithRewards = {
  drop: IDrop
  rewards: {
    claimable: RewardWithHistory[]
    nonClaimable: Reward[]
  }
}

const Drops = () => {
  const dispatch = useDispatch()
  const account = useSelector(selectAccount)
  const { getWriteOnlyContract } = useWeb3()

  const [fetchDropsRewards, { isLoading, isFetching }] =
    useLazyGetDropsRewardsQuery()
  const [drops, setDrops] = useState<
    Array<{
      parentDropId: number
      name: string
      dropsWithRewards: DropWithRewards[]
    }>
  >([])

  const [isClaiming, setIsClaiming] = useState(false)
  const [claimRewardsMutation] = useClaimRewardsSignatureMutation()
  const [claimRewardsHistoryMutation] = useClaimRewardsHistoryMutation()

  const { isLoggedIn } = useIsLoggedIn()
  const { networkId } = useNetworkId()
  const { data: airdropContract } = useGetContractByNameQuery({
    name: 'Airdrop',
    chainId: networkId,
  })

  const fetchDrops = useCallback(async () => {
    const { allDropsWithRewards } = await fetchDropsRewards({}).unwrap()
    if (!allDropsWithRewards) return []
    const result = allDropsWithRewards.reduce((acc, drop) => {
      const parentDropId = drop.drop.parentDropId
      const parentDrop = acc.find((o) => o.parentDropId === parentDropId)
      if (parentDrop) {
        parentDrop.dropsWithRewards.push(drop)
      } else {
        acc.push({
          parentDropId,
          name: drop.drop.name,
          dropsWithRewards: [drop],
        })
      }
      return acc
    }, [])
    setDrops(result)
  }, [fetchDropsRewards])

  useEffect(() => {
    dispatch(setIsDrops(true))
    return () => {
      dispatch(setIsDrops(false))
    }
  }, [dispatch])

  useEffect(() => {
    if (!account) return
    fetchDrops()
  }, [fetchDrops, account])

  const [openTabs, setOpenTabs] = useState<number[]>([])

  useEffect(() => {
    if (drops.length === 0) return
    setOpenTabs([...drops.slice(0, 3).map((_, i) => i)])
  }, [drops])

  const claim = useCallback(
    async (parentDropId: number, chainId: number) => {
      try {
        if (Number(networkId) !== chainId) {
          toast.warning('Please switch network to claim your rewards')
          return
        } else if (!airdropContract) {
          toast.info('Something went wrong. Try refreshing the page')
          return
        }

        setIsClaiming(true)

        let claimData
        try {
          claimData = await claimRewardsMutation({ parentDropId }).unwrap()
        } catch (e) {
          const nonceErrorRegex =
            /Nonce \d+ does not match blockchain nonce \d+/
          if (nonceErrorRegex.test(e?.data?.message)) {
            toast.error('Something went wrong. Please reach out to support')
            console.error(e)
            Sentry.captureException(e)
            return
          }
          throw e
        }

        const {
          parentAirdropId,
          chapterIds,
          erc20RewardsForMultipleChapter,
          nonce,
          signature,
        } = claimData

        if (!signature) throw new Error('No signature returned')
        if (!nonce) throw new Error('No nonce returned')
        if (
          !erc20RewardsForMultipleChapter ||
          erc20RewardsForMultipleChapter.length === 0
        )
          throw new Error('No rewards returned')
        if (!chapterIds || chapterIds.length === 0)
          throw new Error('No chapterIds returned')
        if (!parentAirdropId) throw new Error('No parentAirdropId returned')
        const {
          contract: { abi: airdropAbi, address: airdropContractAddress },
        } = airdropContract

        const ethersContractInstance = getWriteOnlyContract(
          airdropContractAddress,
          airdropAbi.slice() as ethers.InterfaceAbi,
        )

        const args = clone([
          parentAirdropId,
          chapterIds,
          erc20RewardsForMultipleChapter,
          nonce,
          signature,
        ])

        let gas: bigint | undefined
        try {
          gas = await ethersContractInstance.claimRewards.estimateGas(...args)
        } catch (e) {
          console.error('Failed to estimate gas on claim drop rewards', e)
        }

        const transaction = await ethersContractInstance.claimRewards(...args, {
          gasLimit: gas ? gas * 2n : 10000n,
        })

        const { hash: transactionHash } = transaction

        await transaction.wait()

        await claimRewardsHistoryMutation({
          body: {
            parentAirdropId,
            chapterIds,
            erc20RewardsForMultipleChapter,
            transactionHash,
          },
        })

        await fetchDrops()

        toast.success('Claimed!')
      } catch (e) {
        Sentry.captureException(e)
        console.error(e)
        toast.error('Something went wrong')
      } finally {
        setIsClaiming(false)
      }
    },
    [
      networkId,
      airdropContract,
      getWriteOnlyContract,
      claimRewardsHistoryMutation,
      fetchDrops,
      claimRewardsMutation,
    ],
  )

  const loading = isLoading || isFetching

  return (
    <DropsContainer dc>
      {!drops?.length && !loading && (
        <NoAllocations text="If you have participated in an airdrop campaign, your rewards will appear here." />
      )}
      {loading ? (
        <LoadingContainer c>
          <AnimatedLoader onlyWhite size={64} />
        </LoadingContainer>
      ) : (
        drops?.map(({ parentDropId, name, dropsWithRewards }, i) => {
          const isOpen = openTabs.includes(i)
          const logoUrl = dropsWithRewards[0].drop.about.logo.url

          const chainId = Number(dropsWithRewards[0].drop.chainId)
          const claimableRewards = dropsWithRewards.filter(
            ({ rewards }) => rewards.claimable.length > 0,
          )
          const isClaimable = claimableRewards.length > 0
          const isClaimed = claimableRewards.every(({ rewards }) =>
            rewards.claimable.every((r) => !!r?.history),
          )

          return (
            <AllocationCard dc key={parentDropId} isOpen={isOpen}>
              <TopBar>
                <LogoAndTitle align="center">
                  <Logo src={logoUrl} />
                  <Box ac g={4}>
                    <TokenName>{name}</TokenName>
                  </Box>
                </LogoAndTitle>
                <Box fu aw align="center">
                  <ChainIcon
                    src={`/img/icons/chains/${
                      chainId === BLAST.chainId ? 'blast' : 'base'
                    }.svg`}
                  />
                  {isClaimable && (
                    <ClaimButtonBox marginRight={16} faded={isClaimed}>
                      {!isLoggedIn ? (
                        <ConnectWalletButton label="Connect to Claim" />
                      ) : networkId !== chainId ? (
                        <>
                          {chainId === BLAST.chainId ? (
                            <SwitchToBlastButton />
                          ) : (
                            <SwitchToBaseButton />
                          )}
                        </>
                      ) : (
                        <ThemedButton
                          disabled={isClaimed || isClaiming}
                          onClick={() => claim(parentDropId, chainId)}
                          $styleType="primary"
                        >
                          {isClaimed ? (
                            'Claimed'
                          ) : isClaiming ? (
                            <AnimatedLoader size={20} />
                          ) : (
                            'Claim'
                          )}
                        </ThemedButton>
                      )}
                    </ClaimButtonBox>
                  )}
                  <Button
                    isOpen={isOpen}
                    onClick={() => {
                      setOpenTabs((p) =>
                        isOpen ? [...p].filter((id) => id !== i) : [...p, i],
                      )
                    }}
                  >
                    <img
                      style={{ transform: 'rotate(180deg)' }}
                      src="/img/icons/allocation-arrow-down.svg"
                      alt="down arrow"
                    />
                  </Button>
                </Box>
              </TopBar>
              {dropsWithRewards.map(({ drop, rewards }, i) => {
                const { id, chapterName } = drop

                return (
                  <Box dc g={16} key={id}>
                    <TokenName>{chapterName}</TokenName>
                    <Box dc key={i} g={0}>
                      {rewards.claimable.map((reward, j) => (
                        <RewardInfo
                          key={`${i}-${j}-${reward.dropReward.name}`}
                          name={reward.dropReward.name}
                          amount={reward.userReward.amount}
                          rank={reward.userReward.metadata.drop.rank}
                          level={reward.userReward.metadata.drop.level}
                          userRewardId={reward.userReward.id}
                          history={reward?.history}
                          isClaimable
                        />
                      ))}
                      {rewards.nonClaimable.map((reward, j) => (
                        <RewardInfo
                          key={`${i}-${j}-${reward.id}`}
                          name={reward.metadata.drop.reward?.name}
                          amount={reward.amount}
                          rank={reward.metadata.drop.rank}
                          level={reward.metadata.drop.level}
                          userRewardId={reward.id}
                        />
                      ))}
                    </Box>
                  </Box>
                )
              })}

              {!name.toLowerCase().includes('zap') && (
                <Disclaimer>
                  Disclaimer: Rewards are distributed by participating projects
                  with their own vesting schedules and rewards may differ.
                  Implied value of Blast Gold is from BFX Calculator & may vary
                  (
                  <a
                    href="https://zap-launchpad.notion.site/ZAP-Blast-Gigadrops-Terms-and-Conditions-1ffb09aa3041468a821f852baedf3d54"
                    target="_blank"
                    rel="noreferrer"
                  >
                    see T&Cs
                  </a>
                  ).
                </Disclaimer>
              )}
            </AllocationCard>
          )
        })
      )}
    </DropsContainer>
  )
}

export default Drops

const DropsContainer = styled(Box)`
  z-index: 10;
  margin-top: 3rem;
  gap: 2rem;
`

const LoadingContainer = styled(Box)`
  min-height: 200px;
`

const ClaimButtonBox = styled(Box)<{ faded?: boolean }>`
  margin-right: 16px;
  ${({ faded }) =>
    faded &&
    css`
      opacity: 0.3;
    `}
`

/**
 * https://stackoverflow.com/a/31002148
 */
export const getLeadingZeros = (value: number) => {
  return Math.abs(-Math.floor(Math.log10(value) + 1))
}

function RewardInfo({
  amount: _amount,
  name,
  rank,
  level,
  history,
  isClaimable,
  userRewardId,
}: {
  amount: number
  name: string
  rank: number
  level: number
  history?: ClaimHistory
  isClaimable?: boolean
  userRewardId: number
}) {
  const amount = Number(_amount)
  const decimalLimit = !!amount ? getLeadingZeros(amount) + 1 : 0

  return (
    <Container dc g={12} className="inner" marginTop={0} marginBottom={16}>
      <Box>
        <InfoSection>
          <span className="title">Your reward</span>
          <span>
            {showDecimalsIfNeeded(amount, decimalLimit)}{' '}
            <span className="low-opacity">{name}</span>
          </span>
        </InfoSection>
        {/*<InfoSection>*/}
        {/*  <span className="title">Your rank</span>*/}
        {/*  <span>*/}
        {/*    <span className="low-opacity">#</span> {rank}*/}
        {/*  </span>*/}
        {/*</InfoSection>*/}
        {/*<InfoSection isLast={true}>*/}
        {/*  <span className="title">Your level</span>*/}
        {/*  <span>*/}
        {/*    <span className="low-opacity">Level</span> {level}*/}
        {/*  </span>*/}
        {/*</InfoSection>*/}

        {history ? (
          <Box c relative fu aw g={6} padding="0 8px">
            <Claimed c>Claimed</Claimed>
            <img
              src="/img/icons/info-circle.svg"
              alt="info tooltip icon"
              data-tooltip-id={`claimed-${userRewardId}-tooltip`}
            />
            <Tooltip
              id={`claimed-${userRewardId}-tooltip`}
              opacity={1}
              style={{
                maxWidth: '200px',
                zIndex: 1,
              }}
            >
              <TooltipText>
                Claimed on {/* Jun 27th, 2024 at 18:00 PM*/}
                {format(new Date(history.createdAt), 'PPPp')}
              </TooltipText>
            </Tooltip>
          </Box>
        ) : isClaimable ? (
          <Claimable c fu aw>
            Claimable
          </Claimable>
        ) : null}
      </Box>
    </Container>
  )
}

export const Container = styled(Box)`
  padding: 24px 24px 24px 8px;
  gap: 12px;
  height: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: #030b11;
  border-radius: 18px;
  margin: 0 0 16px;
  flex-wrap: wrap;
  align-items: center;
  z-index: 10;

  @media screen and (max-width: 800px) {
    .inner {
      flex-direction: column;
      align-items: flex-start;
      height: auto;
    }
  }
`

export const InfoSection = styled(Box)<{ isLast?: boolean }>`
  flex-direction: column;
  padding: 0 8px;

  ${({ theme, isLast }) =>
    !isLast &&
    css`
      border-right: 1px solid
        ${getRgbaValue(theme.semantics.zap.surface.primary, 0.1)};
    `}
  span.title {
    font-size: 12px;
    line-height: 15px;
    opacity: 0.6;
    margin-bottom: 8px;
  }
  span.low-opacity {
    opacity: 0.6;
  }
  span.pink {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }
  @media screen and (max-width: 800px) {
    border-right: none;
    margin: 16px auto;
    width: 50%;
    flex: auto;
    span.title {
      height: 30px;
    }
  }
`

export const Disclaimer = styled.p`
  margin-top: 8px;

  font-size: 12px;
  line-height: 1.6;
  opacity: 0.7;

  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.brandPrimary};
  }
`

export const Claimable = styled(Box)<{ faded?: boolean }>`
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.brandPrimary};
  height: 47px;
`

export const Claimed = styled(Claimable)`
  opacity: 0.5;
`
